import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "abayle-cookie-consent";

// Initialize optional tracking (PostHog) only when consent is granted.
const initOptionalTracking = () => {
  try {
    if (typeof window === "undefined") return;
    if (!window.posthog || typeof window.posthog.init !== "function") return;
    if (window.__abaylePosthogInitialized) return;
    window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
      api_host: "https://us.i.posthog.com",
      person_profiles: "identified_only",
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
      },
    });
    window.__abaylePosthogInitialized = true;
  } catch (e) {
    // Silent fail — tracking is non-essential
  }
};

export const getConsent = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

// If consent was granted in a previous visit, re-init tracking on app load.
export const hydrateTrackingFromConsent = () => {
  if (getConsent() === "accepted") {
    initOptionalTracking();
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Slight delay so the site loads first
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // ignore storage errors
    }
  };

  const handleAccept = () => {
    persist("accepted");
    initOptionalTracking();
    setVisible(false);
  };

  const handleReject = () => {
    persist("rejected");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6 animate-reveal"
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="mx-auto max-w-5xl bg-abayle-green-dark text-white rounded-lg shadow-2xl border border-abayle-gold/30 overflow-hidden">
        <div className="h-[3px] bg-abayle-gold" />
        <div className="p-5 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-6">
          <div className="flex items-start gap-4 flex-1">
            <div className="shrink-0 h-11 w-11 rounded-md bg-abayle-gold/10 border border-abayle-gold/30 flex items-center justify-center">
              <Cookie className="h-5 w-5 text-abayle-gold" />
            </div>
            <div className="text-sm sm:text-[15px] leading-relaxed text-white/85">
              <div className="text-white font-semibold text-base mb-1">
                We value your privacy
              </div>
              <p>
                Abayle Logistics and Trading uses essential cookies to operate this website and,
                with your consent, analytics cookies to understand how visitors use our site. Read
                our{" "}
                <Link
                  to="/cookies-policy"
                  className="text-abayle-gold hover:text-white underline-offset-4 underline decoration-abayle-gold/60"
                  data-testid="cookie-banner-policy-link"
                >
                  Cookies Policy
                </Link>{" "}
                to learn more.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
            <button
              type="button"
              onClick={handleReject}
              className="inline-flex items-center justify-center bg-transparent text-white border-2 border-white/40 hover:border-white hover:text-white px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200"
              data-testid="cookie-reject"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="inline-flex items-center justify-center bg-abayle-gold text-abayle-green border-2 border-abayle-gold hover:bg-white hover:border-white px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200"
              data-testid="cookie-accept"
            >
              Accept All
            </button>
          </div>

          <button
            type="button"
            onClick={handleReject}
            aria-label="Close cookie banner"
            className="absolute top-3 right-3 p-1.5 rounded-md text-white/60 hover:text-abayle-gold hover:bg-white/5 transition-colors lg:hidden"
            data-testid="cookie-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
